// Fixed-total products (flags, A-Frames, media walls). All prices AUD ex GST, total for the order.
const QTY = (list) => list.map((q) => ({ id: q, label: String(q) }));
const CUSTOM_QTY = { id: "custom", label: "Custom" };

const FLAG_PRICES = {
  "2.4": { spike: { 1: 290, 2: 544, 3: 782, 5: 1178, 10: 2144 }, metal: { 1: 372, 2: 682, 3: 976, 5: 1472, 10: 2684 } },
  "3": { spike: { 1: 346, 2: 656, 3: 946, 5: 1434, 10: 2624 }, metal: { 1: 444, 2: 826, 3: 1188, 5: 1802, 10: 3306 } },
  "4": { spike: { 1: 394, 2: 752, 3: 1088, 5: 1658, 10: 3046 }, metal: { 1: 520, 2: 978, 3: 1412, 5: 2156, 10: 3978 } },
};
const FLAG_BASE_KG = { "2.4": 5, "3": 7, "4": 10 };

const AFRAME_PRICES = {
  permanent: { "600x900": { 1: 404, 2: 754, 5: 1668, 10: 3198 }, "1200x900": { 1: 654, 2: 1254, 5: 2820, 10: 5374 } },
  interchangeable: { "600x900": { 1: 300, 2: 536, 5: 1144, 10: 2192 }, "1200x900": { 1: 496, 2: 900, 5: 1978, 10: 3780 } },
};
export const AFRAME_TYPES = {
  permanent: { label: "Permanent aluminium signs", name: "Aluminium A-Frame — Permanent Aluminium Signs", note: "Aluminium sign panels · graphics are permanent, not interchangeable" },
  interchangeable: { label: "Interchangeable corflute signs", name: "Aluminium A-Frame — Interchangeable Corflute Signs", note: "Removable corflute panels · swap graphics by changing the panels" },
};
const AFRAME_SIZES = { "600x900": "600 × 900 mm", "1200x900": "1200 × 900 mm" };

const MEDIAWALL_SIZES = [
  { id: "1.5", label: "1.5m × 2.2m", price: 624 },
  { id: "2", label: "2m × 2.2m", price: 686 },
  { id: "2.4", label: "2.4m × 2.2m", price: 764 },
  { id: "3", label: "3m × 2.2m", price: 942 },
  { id: "4", label: "4m × 2.2m", price: 1176 },
  { id: "6", label: "6m × 2.2m", price: 1608 },
];

export const FIXED_PRODUCTS = {
  flag: {
    id: "flag",
    path: "/flags",
    name: "Feather Flags",
    shortName: "Feather Flags",
    headline: "Feather Flags",
    tagline: "Full-colour double-sided feather flags — complete kit with printed flag, pole and your choice of base.",
    image: "/products/feather-flags.webp",
    imageAlt: "Feather flag example",
    uses: ["Shop fronts", "Events", "Sports clubs", "Open homes", "Markets", "Promotions"],
    features: ["Feather flags in 2.4m, 3m or 4m", "Full-colour double-sided printing included", "Complete kit: printed flag, pole and base", "Ground spike or flat heavy-duty metal base", "Prices are for the whole order"],
    artworkNote: "Please supply print-ready artwork using our flag template for your chosen size. We'll confirm the layout with you before printing.",
    unitWord: ["flag", "flags"],
    steps: [
      { key: "size", label: "Flag size", options: () => [
        { id: "2.4", label: "2.4m" }, { id: "3", label: "3m" }, { id: "4", label: "4m" },
      ] },
      { key: "base", label: "Base", options: (s) => [
        { id: "spike", label: "Ground spike" },
        { id: "metal", label: `Flat ${FLAG_BASE_KG[s.size]}kg heavy-duty metal base` },
      ] },
      { key: "qty", label: "Quantity", options: () => [...QTY([1, 2, 3, 5, 10]), CUSTOM_QTY] },
    ],
    price: (s) => FLAG_PRICES[s.size]?.[s.base]?.[s.qty],
    cart: (s, opt) => ({
      product_name: "Feather Flag",
      size: `${opt.size.label} feather flag`,
      shape: opt.base.label,
      quantity: s.qty,
      print_sides: "double",
    }),
  },
  aframe: {
    id: "aframe",
    path: "/a-frames",
    name: "Aluminium A-Frames",
    shortName: "A-Frames",
    headline: "Aluminium A-Frames",
    tagline: "Aluminium A-Frame signs printed on both sides. Choose permanent aluminium signs or interchangeable corflute signs.",
    image: "/products/a-frame.webp",
    imageAlt: "Aluminium A-Frame example",
    uses: ["Cafes", "Retail", "Real estate", "Events", "Footpath signage", "Wayfinding"],
    features: ["Aluminium frame", "Printed on both sides", "Permanent aluminium panels or interchangeable corflute panels", "600 × 900 mm or 1200 × 900 mm", "Prices are for the whole order"],
    artworkNote: "Please supply print-ready artwork at your chosen size (600 × 900 mm or 1200 × 900 mm) for both sides, at 300 dpi.",
    unitWord: ["A-Frame", "A-Frames"],
    steps: [
      { key: "type", label: "A-Frame type", cards: true, options: () => Object.entries(AFRAME_TYPES).map(([id, t]) => ({ id, label: t.label, note: t.note })) },
      { key: "size", label: "Size", options: () => Object.entries(AFRAME_SIZES).map(([id, label]) => ({ id, label })) },
      { key: "qty", label: "Quantity", options: () => [...QTY([1, 2, 5, 10]), CUSTOM_QTY] },
    ],
    price: (s) => AFRAME_PRICES[s.type]?.[s.size]?.[s.qty],
    cart: (s, opt) => ({
      product_name: AFRAME_TYPES[s.type].name,
      size: opt.size.label,
      quantity: s.qty,
      print_sides: "double",
    }),
  },
  mediawall: {
    id: "mediawall",
    path: "/media-walls",
    name: "Media Walls",
    shortName: "Media Walls",
    headline: "Media Walls",
    tagline: "Complete media wall kits for events, launches and photo opportunities. Choose your size and upload your artwork.",
    image: "/products/media-wall.webp",
    imageAlt: "Media wall example",
    uses: ["Events", "Product launches", "Trade shows", "Photo walls", "Conferences", "Weddings"],
    features: ["Complete media wall kit", "Six sizes from 1.5m to 6m wide, all 2.2m high", "Full-colour printed graphic", "Order as many media walls as you need"],
    artworkNote: "Please supply print-ready artwork at your chosen size. We'll send you the exact template dimensions and confirm before printing.",
    unitWord: ["media wall", "media walls"],
    steps: [
      { key: "size", label: "Size (width × height)", options: () => MEDIAWALL_SIZES.map(({ id, label }) => ({ id, label })) },
      { key: "qty", label: "Quantity", input: true },
    ],
    price: (s) => (s.qty >= 1 ? MEDIAWALL_SIZES.find((m) => m.id === s.size)?.price * s.qty : undefined),
    cart: (s, opt) => ({
      product_name: "Media Wall",
      size: opt.size.label,
      quantity: s.qty,
    }),
  },
};
